import React, { useState } from "react";
import {
  Box,
  Typography,
  makeStyles,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  FormGroup,
} from "@material-ui/core";
import AppButton from "components/CustomButtons/AppButton";
import DocumentVersionsModal from "../../Client/form/DocumentVersionsModal";
import AppColor from "utilities/AppColor";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  updateCompliance,
  fetchWorkerDetail,
  updateStatusFileTraining,
} from "store/slices/worker";
import AppInput from "components/Input/AppInput";
import CustomDatePicker from "components/Input/CustomDatePicker";
import SelectField from "components/Select/SelectField";
import { COMPLIANCE_STATE } from "config/AppConfig";
import { YupValidations } from "utils/validate";
import { getAuthInfo } from "store/slices/auth";
import moment from "moment";
import { showToast } from "store/slices/toast";
import StaffUploadTrainingModal from "../modal/StaffUploadTrainingModal";
import snakecaseKeys from "snakecase-keys";
import { SYSTEM_ROLE } from "config/AppConfig";
import { FILE_STATUS } from "config/AppConfig";

const useStyles = makeStyles(() => ({
  sectionTitle: {
    fontSize: 13,
    fontWeight: 800,
    color: "#0D9488",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    margin: "10px 0 15px",
    paddingBottom: 5,
    borderBottom: "2px solid #CCFBF1",
  },
  formRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 15,
    marginBottom: 16,
  },
  formRow3: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: 15,
    marginBottom: 16,
  },
  label: {
    fontSize: 11,
    fontWeight: 500,
    color: "#374151",
    marginBottom: 5,
    display: "block",
  },
  alertBox: {
    background: "#F0FDFA",
    border: "1px solid #99F6E4",
    color: "#0D9488",
    borderRadius: 6,
    padding: "9px 11px",
    fontSize: 11,
    marginBottom: 10,
    display: "flex",
    gap: 7,
    alignItems: "flex-start",
    lineHeight: 1.5,
  },
  donePill: {
    background: "#CCFBF1",
    color: "#0F766E",
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    padding: "2px 7px",
    borderRadius: 5,
    fontSize: 11,
    fontWeight: 500,
    height: 24,
    boxSizing: "border-box",
    whiteSpace: "nowrap",
  },
  warningPill: {
    background: "#FEF3C7",
    color: "#92400E",
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    padding: "2px 7px",
    borderRadius: 5,
    fontSize: 11,
    fontWeight: 500,
    height: 24,
    boxSizing: "border-box",
    whiteSpace: "nowrap",
  },
  overduePill: {
    background: "#FEE2E2",
    color: "#991B1B",
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    padding: "2px 7px",
    borderRadius: 5,
    fontSize: 11,
    fontWeight: 500,
    height: 24,
    boxSizing: "border-box",
    whiteSpace: "nowrap",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 12,
  },
  tableHead: {
    background: "#FAFAFA",
  },
  tableCell: {
    padding: "8px 10px",
    textAlign: "left",
    fontWeight: 600,
    color: "#6B7280",
    borderBottom: "1px solid #E5E7EB",
    fontSize: 11,
    whiteSpace: "nowrap",
  },
  tableBodyCell: {
    padding: "8px 10px",
    borderBottom: "1px solid #F3F4F6",
    color: "#111827",
    verticalAlign: "middle",
  },
  control: {
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 20,
    marginBottom: 20,
  },
  highlightRow: {
    backgroundColor: "#EFF6FF", // Light blue background for uploading items
  },
}));

const ComplianceTab = ({ staffDetail, onChange }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const authInfo = useSelector(getAuthInfo);
  const isAdmin = authInfo?.role === SYSTEM_ROLE.COMPANY;

  const [localUploads, setLocalUploads] = useState({});
  const [deletedFiles, setDeletedFiles] = useState({});
  const [serverFileEdits, setServerFileEdits] = useState({});
  const [editingContext, setEditingContext] = useState(null);

  const [showVersionsModal, setShowVersionsModal] = useState(false);
  const [versionsData, setVersionsData] = useState([]);
  const [versionsLocalFiles, setVersionsLocalFiles] = useState([]);
  const [versionsMetadata, setVersionsMetadata] = useState(null);


  const hiddenFileInputRef = React.useRef(null);

  const {
    id = null,
    complianceStatus = "",
    clearanceNumber = "",
    state = "",
    issueDate = "",
    ndisExpireDate = "",
    approvalStatus,
  } = staffDetail || {};

  const initForm = {
    complianceStatus,
    clearanceNumber,
    state,
    issueDate,
    ndisExpireDate,
  };

  const validationSchema = Yup.object().shape({
    clearanceNumber: Yup.string().nullable(),
    state: Yup.string().nullable(),
    issueDate: YupValidations.requiredString,
    complianceStatus: YupValidations.requiredString,
    state: YupValidations.requiredString,
    ndisExpireDate: YupValidations.requiredString,
  });

  const trainingCategories = [
    {
      key: "workerNdisSupportingSafeAndEnjoyableMealsResponseList",
      keyUpdate: "ndis_supporting_safe_and_enjoyable_meals",
      label: t("trainingTag.workerNdisSupportingSafeAndEnjoyableMeals"),
      fileNameKey: "fileName",
      fileArrayKey: "ndisSupportingSafeAndEnjoyableMealsArray",
      fileLinkKey: "fileNameAws3Link",
      generalName: "generalName",
      needsExpiry: false,
    },
    {
      key: "workerSupportingPeopleToStayInfectionFreeResponseList",
      keyUpdate: "supporting_people_to_stay_infection_free",
      label: t("trainingTag.workerSupportingPeopleToStayInfectionFree"),
      fileNameKey: "fileName",
      fileArrayKey: "supportingPeopleToStayInfectionFreeArray",
      fileLinkKey: "fileNameAws3Link",
      generalName: "generalName",
      needsExpiry: false,
    },
    {
      key: "workerSupportingEffectiveCommunicationResponseList",
      keyUpdate: "supporting_effective_communication",
      label: t("trainingTag.workerSupportingEffectiveCommunication"),
      fileNameKey: "fileName",
      fileArrayKey: "supportingEffectiveCommunicationArray",
      fileLinkKey: "fileNameAws3Link",
      generalName: "generalName",
      needsExpiry: false,
    },
    {
      key: "workerHandHygieneResponseList",
      keyUpdate: "hand_hygiene",
      label: t("trainingTag.workerHandHygiene"),
      fileNameKey: "fileName",
      fileArrayKey: "handHygieneArray",
      fileLinkKey: "fileNameAws3Link",
      generalName: "generalName",
      needsExpiry: false,
    },
    {
      key: "workerSafeFoodHandlingResponseList",
      keyUpdate: "safe_food_handling",
      label: t("trainingTag.workerSafeFoodHandling"),
      fileNameKey: "fileName",
      fileArrayKey: "safeFoodHandlingArray",
      fileLinkKey: "fileNameAws3Link",
      generalName: "generalName",
      needsExpiry: false,
    },
    {
      key: "workerOtherResponseList",
      keyUpdate: "other_file_list",
      label: t("trainingTag.workerOther"),
      fileNameKey: "fileName",
      fileArrayKey: "otherFileArray",
      fileLinkKey: "otherFileAws3Link",
      generalName: "generalName",
      needsExpiry: false,
      isMultiple: true,
    },
  ];

  const mapCategoryToComplianceType = (key) => {
    switch (key) {
      case "workerNdisSupportingSafeAndEnjoyableMealsResponseList":
        return "WORKER_NDIS_SUPPORTING_SAFE_AND_ENJOYABLE_MEALS";
      case "workerSupportingPeopleToStayInfectionFreeResponseList":
        return "WORKER_SUPPORTING_PEOPLE_TO_STAY_INFECTION_FREE";
      case "workerSupportingEffectiveCommunicationResponseList":
        return "WORKER_SUPPORTING_EFFECTIVE_COMMUNICATION";
      case "workerHandHygieneResponseList":
        return "WORKER_HAND_HYGIENE";
      case "workerSafeFoodHandlingResponseList":
        return "WORKER_SAFEFOOD_HANDLING";
      case "workerOtherResponseList":
        return "WORKER_OTHER";
      default:
        return null;
    }
  };

  const getFileLink = (categoryKey, item) => {
    if (!item) return "";
    const map = {
      workerAgreementFileResponseList: item.agreementFileAws3Link,
      workerCarRegistrationResponseList: item.carRegistrationFileAws3Link,
      workerCertificationResponseList: item.certificationFileAws3Link,
      workerComprehensiveInsuranceResponseList:
        item.comprehensiveInsuranceFileAws3Link,
      workerCprDocResponseList: item.cprFileAws3Link,
      workerDriverLicenceResponseList: item.workerDriverLicenceFileAws3Link,
      workerHandHygieneResponseList: item.fileNameAws3Link,
      workerInfectionControlResponseList: item.infectionControlFileAws3Link,
      workerMedicationManagementResponseList: item.fileNameAws3Link,
      workerNdisOrientationModuleResponseList:
        item.ndisOrientationModuleFileAws3Link,
      workerNdisSupportingSafeAndEnjoyableMealsResponseList:
        item.fileNameAws3Link,
      workerNdisWorkerScreeningCheckResponseList:
        item.ndisWorkerScreeningCheckFileAws3Link,
      workerResumeResponseList: item.resumeFileAws3Link,
      workerRightToWorkInAustraliaResponseList:
        item.rightToWorkInAustraliaFileAws3Link,
      workerSafeFoodHandlingResponseList: item.fileNameAws3Link,
      workerSupportingEffectiveCommunicationResponseList: item.fileNameAws3Link,
      workerSupportingPeopleToStayInfectionFreeResponseList:
        item.fileNameAws3Link,
      workerWorkingChildrenDocResponseList: item.workingChildrenDocFileAws3Link,
    };
    return map[categoryKey] || item.fileNameAws3Link || "";
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    const formData = new FormData();

    const normalizeDate = (val) => {
      if (!val) return val;
      const parsed = moment(
        val,
        ["YYYY/MM/DD", "DD/MM/YYYY", "YYYY-MM-DD"],
        false,
      );
      return parsed.isValid() ? parsed.format("YYYY/MM/DD") : val;
    };

    // JSON Request String
    const updateComplianceRequest = {
      complianceStatus: values.complianceStatus,
      clearanceNumber: values.clearanceNumber,
      state: values.state,
      issueDate: normalizeDate(values.issueDate),
      ndisExpireDate: normalizeDate(values.ndisExpireDate),
    };

    // If Infection Control has a local upload with expiry, use that if the expiry date has changed or a file is uploaded.
    const localInfControl = localUploads["workerInfectionControlResponseList"];
    if (localInfControl?.expiryDate) {
      const list = staffDetail ? staffDetail["workerInfectionControlResponseList"] : [];
      const sorted = list ? [...list].sort((a, b) => moment(b.createdDate || b.created_date).valueOf() - moment(a.createdDate || a.created_date).valueOf()) : [];
      const originalItem = sorted.length > 0 ? sorted[0] : null;
      const originalExpiry = originalItem?.expireDate || originalItem?.expiryDate || "";

      const formatForComparison = (dateStr) => {
        if (!dateStr) return "";
        let m = moment(dateStr, moment.ISO_8601);
        if (!m.isValid()) {
          m = moment(dateStr, ["YYYY/MM/DD", "DD/MM/YYYY", "YYYY-MM-DD"]);
        }
        return m.isValid() ? m.format("YYYY-MM-DD") : "";
      };

      const isFileUploaded = !!localInfControl?.file;
      const isExpiryChanged = formatForComparison(localInfControl.expiryDate) !== formatForComparison(originalExpiry);

      if (isFileUploaded || isExpiryChanged) {
        updateComplianceRequest.infectionControlExpireDate = normalizeDate(
          localInfControl.expiryDate,
        );
      }
    }

    // Deactivate Compliance items
    const deactivateCompliance = [];
    Object.keys(deletedFiles).forEach((complianceType) => {
      const fileIds = deletedFiles[complianceType];
      if (fileIds && fileIds.length > 0) {
        deactivateCompliance.push({
          complianceType: complianceType,
          fileIds: fileIds,
        });
      }
    });
    if (deactivateCompliance.length > 0) {
      updateComplianceRequest.deactivateCompliance = deactivateCompliance;
    }

    // Other items for workerOtherResponseList
    const otherUpload = localUploads["workerOtherResponseList"];
    if (otherUpload && Array.isArray(otherUpload.file) && otherUpload.file.length > 0) {
      updateComplianceRequest.otherFilesMetadata = otherUpload.file.map((item) => ({
        fileName: item.name,
        expireDate: normalizeDate(item.expireDate),
      }));
    }

    // Server file edits for existing files (updateOtherFiles)
    const otherFileEdits = serverFileEdits["WORKER_OTHER"];
    if (otherFileEdits && Object.keys(otherFileEdits).length > 0) {
      updateComplianceRequest.updateOtherFiles = Object.entries(otherFileEdits).map(([fileId, edits]) => ({
        fileId: parseInt(fileId),
        fileName: edits.name,
        expireDate: normalizeDate(edits.expireDate),
      }));
    }

    formData.append(
      "content-data",
      new Blob([JSON.stringify(snakecaseKeys(updateComplianceRequest))], {
        type: "application/json",
      }),
    );

    // Append Files
    Object.keys(localUploads).forEach((catKey) => {
      const uploadData = localUploads[catKey];
      if (uploadData && uploadData.file) {
        const category = trainingCategories.find((c) => c.key === catKey);
        if (category) {
          if (Array.isArray(uploadData.file)) {
            uploadData.file.forEach((item) => {
              if (item.file) {
                formData.append(category.keyUpdate, item.file);
              }
            });
          } else {
            formData.append(category.keyUpdate, uploadData.file);
          }
        }
      }
    });

    let disableSuccessMessage = false;
    const pendingStatusUpdates = Object.values(localUploads).filter(
      (u) => u.status && u.fileId && u.status !== u.originalStatus,
    );
    if (pendingStatusUpdates.length > 0) {
      disableSuccessMessage = true;
    }

    const result = await dispatch(
      updateCompliance({ id, formData, disableSuccessMessage }),
    );
    if (updateCompliance.fulfilled.match(result)) {
      
      if (pendingStatusUpdates.length > 0) {
        for (const update of pendingStatusUpdates) {
          await dispatch(
            updateStatusFileTraining({
              type: update.documentType,
              fileId: update.fileId,
              status: update.status,
              workerId: id,
              disableSuccessMessage: true,
            }),
          );
        }
        dispatch(
          showToast({ type: "success", message: t("toast.update.200") }),
        );
      }
      setLocalUploads({});
      setServerFileEdits({});
      if (onChange && id) {
        const freshDetail = await dispatch(fetchWorkerDetail({ workerId: id }));
        if (freshDetail.payload) {
          onChange(freshDetail.payload);
        }
      }
    }
    setSubmitting(false);
  };

  const statusOptions = [
    { value: "CLEARED", label: t("complianceStatus.CLEARED") },
    { value: "PENDING", label: t("complianceStatus.PENDING") },
    { value: "EXCLUDED", label: t("complianceStatus.EXCLUDED") },
    { value: "INTERIM_BAR", label: t("complianceStatus.INTERIM_BAR") },
    { value: "EXPIRED", label: t("complianceStatus.EXPIRED") },
    { value: "NOT_SUBMITTED", label: t("complianceStatus.NOT_SUBMITTED") },
  ];

  const stateOptions = Object.values(COMPLIANCE_STATE).map((s) => ({
    value: s,
    label: t(`complianceState.${s}`),
  }));

  const triggerFileSelect = (category) => {
    setEditingContext({ type: "UPLOAD", category, file: null });
    if (hiddenFileInputRef.current) {
      hiddenFileInputRef.current.value = null;
      hiddenFileInputRef.current.click();
    }
  };

  const handleGlobalFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0 || !editingContext?.category) return;

    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/jpg",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    const invalidFile = files.find((f) => !allowedTypes.includes(f.type));
    if (invalidFile) {
      dispatch(
        showToast({
          type: "error",
          message: t("validation.invalidFileImage"),
        }),
      );
      return;
    }

    const category = editingContext.category;

    if (category.isMultiple || category.key === "workerOtherResponseList") {
      const list = staffDetail ? staffDetail[category.key] : [];
      const sorted = list ? [...list].sort((a, b) => moment(b.createdDate || b.created_date).valueOf() - moment(a.createdDate || a.created_date).valueOf()) : [];
      const item = sorted.length > 0 ? sorted[0] : null;

      const existingLocal = localUploads[category.key]?.file || [];
      const existingArray = Array.isArray(existingLocal) ? existingLocal : (existingLocal ? [existingLocal] : []);

      const newFiles = files.map((f) => ({
        file: f,
        name: f.name.replace(/\.[^/.]+$/, ""),
        expireDate: "",
      }));

      const combined = [...newFiles, ...existingArray];

      setEditingContext({
        type: "EDIT",
        category,
        file: combined,
        item,
        status: item?.status || "",
      });
      return;
    }

    const file = files[0];
    if (category.needsExpiry) {
      setEditingContext((prev) => ({ ...prev, file }));
    } else {
      setLocalUploads({
        ...localUploads,
        [category.key]: {
          ...localUploads[category.key],
          file: file,
          expiryDate: "",
        },
      });
      setEditingContext(null);
    }
  };

  const confirmModalUpload = (
    file,
    expiryDate,
    selectedCategoryKey,
    status,
    finalDeletedIds = [],
    fileEdits = {},
  ) => {
    const targetCategoryKey =
      selectedCategoryKey || editingContext?.category?.key;
    if (!targetCategoryKey) return;

    const targetComplianceType = mapCategoryToComplianceType(targetCategoryKey);
    const existing = localUploads[targetCategoryKey] || {};

    setLocalUploads({
      ...localUploads,
      [targetCategoryKey]: {
        ...existing,
        file: file || existing.file,
        expiryDate: expiryDate,
        status: (Array.isArray(file) ? file.length > 0 : !!file) ? null : status || existing.status,
        originalStatus: existing.originalStatus || editingContext?.status,
        fileId: existing.fileId || editingContext?.item?.id,
        complianceType: targetComplianceType,
        documentType:
          existing.documentType ||
          mapCategoryToComplianceType(targetCategoryKey),
      },
    });

    if (targetComplianceType) {
      setDeletedFiles((prev) => ({
        ...prev,
        [targetComplianceType]: finalDeletedIds,
      }));

      // Store server file edits
      if (Object.keys(fileEdits).length > 0) {
        setServerFileEdits((prev) => ({
          ...prev,
          [targetComplianceType]: fileEdits,
        }));
      }
    }

    setEditingContext(null);
  };

  const getRowData = (category) => {
    const list = staffDetail ? staffDetail[category.key] : [];
    const sorted = list ? [...list].sort((a, b) => moment(b.createdDate || b.created_date).valueOf() - moment(a.createdDate || a.created_date).valueOf()) : [];
    const item = sorted.length > 0 ? sorted[0] : null;

    const local = localUploads[category.key];
    if (local) {
      const data = {
        training: category.label,
        status: local.file ? "UPLOADING" : (local.status || "UPLOADING"),
        completed: "-",
        isUploading: true,
        category,
        file: local.file,
        item,
      };
      return data;
    }

    if (!item) {
      return {
        training: category.label,
        status: "NOT_UPLOADED",
        completed: "-",
        rawExpiry: null,
        isUploading: false,
        category,
      };
    }

    let statusDisplay = item.status || "PENDING";
    let pillClass = '';

    const itemExpiry = item.expiryDate || item.expireDate || null;
    if (itemExpiry) {
      const expMoment = moment(itemExpiry);
      const now = moment();
      const diffDays = expMoment.diff(now, "days");

      if (diffDays < 0) {
        statusDisplay = FILE_STATUS.OVERDUE;
        pillClass = classes.overduePill;
      } else if (diffDays <= 90) {
        pillClass = classes.warningPill;
      }
    }

    let finalStatus = statusDisplay;
    let finalCompleted = item.status === "VERIFIED" && item.updatedDate
      ? moment(item.updatedDate).format("DD/MM/YYYY")
      : "-";

    return {
      training: category.label,
      status: finalStatus,
      completed: finalCompleted,
      rawExpiry: itemExpiry,
      pillClass,
      isUploading: false,
      category,
      item,
    };
  };

  const renderStatus = (rowData) => {
    if (rowData.status === "-") {
      return <span>-</span>;
    }
    let className = '';
    switch (rowData.status) {
      case FILE_STATUS.UPLOADING:
        className = classes.warningPill;
        break;
      case FILE_STATUS.OVERDUE:
        className = classes.overduePill;
        break;
      case FILE_STATUS.NOT_UPLOADED:
        className = classes.overduePill;
        break;
      case FILE_STATUS.PENDING:
      case FILE_STATUS.REVIEW:
        className = classes.warningPill;
        break;
      default:
        className = classes.donePill;
        break;
    }
    return (
      <span className={className}>
        {t(`fileStatus.${rowData.status}`)}
      </span>
    );
  };

  return (
    <Box>
      <Formik
        enableReinitialize={true}
        initialValues={initForm}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form>
            <Typography className={classes.sectionTitle}>
              {t("title.ndisWorkerScreening")}
            </Typography>
            <div className="row">
              <div className="col-sm-12 col-md-6">
                <Field
                  component={SelectField}
                  name="complianceStatus"
                  label={"label.status"}
                  options={statusOptions}
                  required
                  placeholder={t("label.status")}
                />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field
                  component={AppInput}
                  name="clearanceNumber"
                  label={t("label.clearanceNumber")}
                />
              </div>
              <div className="col-sm-12 col-md-4">
                <Field
                  component={SelectField}
                  name="state"
                  label={"label.state"}
                  options={stateOptions}
                  required
                  placeholder={t("label.state")}
                />
              </div>
              <div className="col-sm-12 col-md-4">
                <Field
                  component={CustomDatePicker}
                  label={t("label.issueDate")}
                  name="issueDate"
                  type="text"
                  id="issueDate"
                  formatDateJob={true}
                  classInput={"date-picker-container-custom"}
                  required
                />
              </div>
              <div className="col-sm-12 col-md-4">
                <Field
                  component={CustomDatePicker}
                  label={t("label.expiryDate")}
                  name="ndisExpireDate"
                  type="text"
                  id="ndisExpireDate"
                  formatDateJob={true}
                  classInput={"date-picker-container-custom"}
                  required
                />
              </div>
            </div>

            <Box className={classes.alertBox}>
              <i className="fas fa-bell" style={{ fontSize: 14 }}></i>
              <span>{t("alert.expiryAlert", { staffName: `${staffDetail?.firstName} ${staffDetail?.lastName}` })}</span>
            </Box>

            <Typography
              className={classes.sectionTitle}
              style={{ marginTop: 20 }}
            >
              {t("title.trainingQualifications")}
            </Typography>

            <Box style={{ overflowX: "auto" }}>
              <Table className={classes.table}>
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell className={classes.tableCell}>
                      {t("label.training")}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {t("label.status")}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {t("label.completed")}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {t("label.action")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {trainingCategories.map((cat) => {
                    const rowData = getRowData(cat);

                    let showUpload = false;
                    let showView = false;
                    let showUploadRenewal = false;
                    let showUpdateLocal = false;
                    let showChangeLocal = false;
                    let showEditStatus = false;

                    // Show view action if there's server data OR local file
                    showView = !!(rowData.item?.id || rowData.file);

                    showUpload =
                      rowData.status === "NOT_UPLOADED" ||
                      rowData.status === FILE_STATUS.PENDING ||
                      rowData.status === FILE_STATUS.REVIEW ||
                      isAdmin;

                    if (isAdmin) {
                      if (rowData.isUploading) {
                        if (
                          cat.needsExpiry ||
                          cat.isMultiple ||
                          (rowData.item?.id && !rowData.file)
                        ) {
                          showUpdateLocal = true;
                        } else {
                          showChangeLocal = true;
                        }
                      } else {
                        if (
                          rowData.item?.id ||
                          (cat.isMultiple && staffDetail[cat.key]?.length > 0) ||
                          (rowData.status === FILE_STATUS.PENDING || cat.needsExpiry)
                        ) {
                          showEditStatus = true;
                        }
                      }
                    } else {
                      if (
                        rowData.status !== "NOT_UPLOADED" &&
                        rowData.status !== FILE_STATUS.PENDING &&
                        rowData.status !== FILE_STATUS.REVIEW
                      ) {
                        const diffDays = rowData.rawExpiry
                          ? moment(rowData.rawExpiry).diff(moment(), "days")
                          : 999;
                        if (
                          diffDays <= 90 ||
                          rowData.status === "OVERDUE" ||
                          rowData.item?.status === FILE_STATUS.EXPIRED
                        ) {
                          showUploadRenewal = true;
                        }
                      }

                      if (rowData.isUploading) {
                        if (cat.needsExpiry || cat.isMultiple) {
                          showUpdateLocal = true;
                        } else {
                          showChangeLocal = true;
                        }
                      } else if ((cat.needsExpiry || cat.isMultiple) && ((rowData.item?.id && rowData.item?.status === FILE_STATUS.PENDING) || (cat.isMultiple && staffDetail[cat.key]?.length > 0))) {
                        showUpdateLocal = true;
                      }
                    }

                    return (
                      <TableRow
                        key={cat.key}
                        className={
                          rowData.isUploading ? classes.highlightRow : ""
                        }
                      >
                        <TableCell className={classes.tableBodyCell}>
                          {rowData.training}
                        </TableCell>
                        <TableCell className={classes.tableBodyCell}>
                          {renderStatus(rowData)}
                        </TableCell>
                        <TableCell className={classes.tableBodyCell}>
                          {rowData.isUploading ? "" : rowData.completed}
                        </TableCell>
                        <TableCell
                          className={classes.tableBodyCell}
                          style={{
                            display: "flex",
                            gap: 16,
                            alignItems: "center",
                            height: 41,
                          }}
                        >
                          {(showUpload ||
                            showUploadRenewal ||
                            showChangeLocal) && (
                              <i
                                className="fas fa-upload"
                                title={t("button.upload")}
                                style={{
                                  cursor: "pointer",
                                  color: AppColor.mainColor,
                                  fontSize: 16,
                                }}
                                onClick={() => {
                                  if (cat.isMultiple) {
                                    setEditingContext({
                                      type: "EDIT",
                                      category: cat,
                                      item: rowData.item,
                                      status: rowData.status,
                                    });
                                  } else {
                                    triggerFileSelect(cat);
                                  }
                                }}
                              />
                            )}
                          {(showUpdateLocal || showEditStatus) && (
                            <i
                              className="fas fa-edit"
                              title={t("button.edit")}
                              style={{
                                cursor: "pointer",
                                color: AppColor.orange,
                                fontSize: 16,
                              }}
                              onClick={() => {
                                setEditingContext({
                                  type: "EDIT",
                                  category: cat,
                                  item: rowData.item,
                                  status: rowData.status,
                                });
                              }}
                            />
                          )}
                          {showView && (
                            <i
                              className="fas fa-eye"
                              title={t("button.view")}
                              style={{
                                cursor: "pointer",
                                color: "#6B7280",
                                fontSize: 16,
                              }}
                              onClick={() => {
                                const list = staffDetail[cat.key] || [];
                                setVersionsData([...list]);

                                // Collect local files for this category
                                const localFiles = [];
                                const localUpload = localUploads[cat.key];
                                if (localUpload?.file) {
                                  const files = Array.isArray(localUpload.file) ? localUpload.file : [localUpload.file];
                                  files.forEach(f => {
                                    if (f?.file || f) {
                                      localFiles.push({
                                        fileName: f.name,
                                        fileUrl: URL.createObjectURL(f?.file || f),
                                      });
                                    }
                                  });
                                }
                                setVersionsLocalFiles(localFiles);

                                setVersionsMetadata({
                                  title: cat.label,
                                  fileNameKey: cat.fileNameKey,
                                  fileLinkKey: cat.fileLinkKey,
                                });
                                setShowVersionsModal(true);
                              }}
                            />

                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>

            <FormGroup className={classes.control}>
              <AppButton
                label={t("button.save_changes")}
                rounder
                type="submit"
                isLoading={isSubmitting}
                disabled={isSubmitting}
                buttonColor={AppColor.mainColor}
              />
            </FormGroup>

            <input
              ref={hiddenFileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/jpg,application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              style={{ display: "none" }}
              onChange={handleGlobalFileChange}
            />

            <StaffUploadTrainingModal
              key={editingContext ? `${editingContext.type}-${editingContext.category?.key || 'global'}-${editingContext.item?.id || 'new'}` : 'closed'}
              open={
                !!editingContext &&
                (editingContext.type !== "UPLOAD" || !!editingContext.file)
              }
              fileTypeLabel={editingContext?.category?.label}
              initialFile={
                (editingContext?.category?.key &&
                  localUploads[editingContext.category.key]?.file) ||
                editingContext?.file ||
                null
              }
              initialExpiry={
                (editingContext?.category?.key &&
                  localUploads[editingContext.category.key]?.expiryDate) ||
                editingContext?.item?.expireDate ||
                editingContext?.item?.expiryDate ||
                ""
              }
              initialDeletedIds={
                editingContext?.category?.key
                  ? (deletedFiles[mapCategoryToComplianceType(editingContext.category.key)] || [])
                  : []
              }
              initialServerFileEdits={
                editingContext?.category?.key
                  ? (serverFileEdits[mapCategoryToComplianceType(editingContext.category.key)] || {})
                  : {}
              }
              initialStatus={
                (editingContext?.category?.key &&
                  localUploads[editingContext.category.key]?.status) ||
                editingContext?.status ||
                ""
              }
              generalName={editingContext?.item?.generalName}
              fileNameSelected={
                editingContext?.item?.fileName ||
                editingContext?.item?.fileNameAws3Link?.split("/").pop() ||
                editingContext?.item?.[editingContext?.category?.fileNameKey] ||
                getFileLink(editingContext?.category?.key, editingContext?.item)
                  ?.split("/")
                  .pop() ||
                null
              }
              item={editingContext?.item}
              items={editingContext?.category?.key ? (staffDetail[editingContext.category.key] || []) : []}
              fileNameKey={editingContext?.category?.fileNameKey}
              fileLinkKey={editingContext?.category?.fileLinkKey}
              isAdmin={isAdmin}
              needsExpiry={editingContext?.category?.needsExpiry}
              isMultiple={editingContext?.category?.isMultiple || false}
              onClose={() => {
                setEditingContext(null);
              }}
              onConfirm={confirmModalUpload}
            />

            {/* <FileViewerModal
              open={showViewer}
              onClose={() => setShowViewer(false)}
              srcFile={viewerFile}
            /> */}

            <DocumentVersionsModal
              open={showVersionsModal}
              onClose={() => {
                setShowVersionsModal(false);
                setVersionsMetadata(null);
                setVersionsLocalFiles([]);
              }}
              details={versionsData}
              localFiles={versionsLocalFiles}
              documentName={versionsMetadata?.title || ""}
              fileNameKey={versionsMetadata?.fileNameKey || ""}
              fileLinkKey={versionsMetadata?.fileLinkKey || ""}
            />
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default ComplianceTab;
